<script setup lang="ts">
type Props = {
  class?: string
  defaultHeight?: number
}

const props = withDefaults(defineProps<Props>(), {
  defaultHeight: 12.5
})

const expanded = ref(false)
const expandable = ref(false)

const contentEl = ref<HTMLDivElement | null>(null)
const contentInnerEl = ref<HTMLDivElement | null>(null)

const toggleExpanded = () => {
  if (!contentEl.value || !contentInnerEl.value) return

  const contentInnerHeight = contentInnerEl.value.scrollHeight

  if (expanded.value) {
    contentEl.value.style.height = `${props.defaultHeight}rem`
  } else {
    contentEl.value.style.height = `${contentInnerHeight}px`
  }

  expanded.value = !expanded.value
}

onMounted(() => {
  if (!contentEl.value || !contentInnerEl.value) return

  const contentInnerHeight = contentInnerEl.value.scrollHeight
  if (contentInnerHeight > props.defaultHeight * 16) {
    expandable.value = true
    contentEl.value.style.height = `${props.defaultHeight}rem`
  }
})

const classes = computed(() => ({
  'app-preview-content': true,
  'app-preview-content--expanded': expanded.value || !expandable.value,
  [`${props.class}`]: !!props.class,
}))
</script>

<template>
  <div :class="classes">
    <div ref="contentEl" class="app-preview-content__content" :style="{ height: defaultHeight }">
      <div ref="contentInnerEl" class="app-preview-content__content-inner">
        <slot></slot>
      </div>
    </div>

    <AppButton
      v-if="expandable"
      variant="text"
      class="mt-10"
      @click="toggleExpanded"
    >
      {{ expanded ? $t('string.Show-less') : $t('string.Show-more') }}

      <template #icon-end-circle>
        <IconMinus v-if="expanded" />
        <IconPlus v-else />
      </template>
    </AppButton>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-preview-content__content {
  position: relative;
  height: auto;
  overflow: hidden;
  @include transition('height', 0.5s);

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 25%;
    background: linear-gradient(
        rgba($white, 0) 0%,
        rgba($white, 0.5) 33%,
        $white
      )
      100%;
    @include transition('opacity', 0.5s);
  }
}

.app-preview-content--expanded {
  .app-preview-content__content {
    position: relative;
    overflow: hidden;

    &::before {
      opacity: 0;
    }
  }
}

.app-preview-content__content-inner {
  ul {
    padding-left: revert;
    list-style-type: revert;
  }
}
</style>
